import * as React from "react";
import { useEffect, useState } from "react";
import { Button, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";

import { ColegioUsuariosService } from "../colegioUsuarios/ColegioUsuariosService";
import { BurlingtonOperationResult } from "../models/dataModels";
import { I18n } from "../utils/I18n";
import { logAndExtractInfoFromException } from "../utils/ValuesFormatter";
import { LicenciasColegioService } from "./LicenciasColegioService";

export interface BorrarUsuarioBurlingtonModalProps {
    /** Usuario cuyo login se propone como email; null para escribirlo a mano. */
    usuarioId: number | null;
    /** Usuario al que se asocia la traza si el email no es el login de nadie del colegio (sus licencias no se tocan). */
    usuarioIdTraza?: number | null;
    /** Se invoca al cerrar el modal; `borrado` indica si Burlington aceptó el borrado (para refrescar la licencia). */
    onClose: (borrado: boolean) => void;
}

type Fase = 'cargando' | 'edicion' | 'borrando' | 'resultado';

/**
 * Borra un usuario en Burlington a partir de su email (el login del usuario en el colegio). Si se indica
 * `usuarioId`, el email llega relleno con su login y se puede cambiar; si no, se escribe a mano.
 */
export function BorrarUsuarioBurlingtonModal({ usuarioId, usuarioIdTraza, onClose }: BorrarUsuarioBurlingtonModalProps) {
    const [fase, setFase] = useState<Fase>(usuarioId == null ? 'edicion' : 'cargando');
    const [email, setEmail] = useState('');
    const [resultado, setResultado] = useState<BurlingtonOperationResult | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (usuarioId == null) {
            return;
        }

        new ColegioUsuariosService().getById(usuarioId)
            .then(usuario => setEmail((usuario.login ?? '').trim()))
            .catch(err => setError(logAndExtractInfoFromException(err)))
            .finally(() => setFase('edicion'));
    }, [usuarioId]);

    const emailLimpio = email.trim();

    const borrar = () => {
        setError(null);
        setFase('borrando');
        new LicenciasColegioService().deleteBurlington(emailLimpio, usuarioIdTraza)
            .then(resp => {
                setResultado(resp);
                setFase('resultado');
            })
            .catch(err => {
                setError(logAndExtractInfoFromException(err));
                setFase('edicion');
            });
    };

    const cerrar = () => onClose(resultado?.isOk === true);

    return <Modal isOpen={true} size="lg" toggle={fase === 'borrando' ? undefined : cerrar}>
        <ModalHeader close={<button className="btn-close" disabled={fase === 'borrando'} onClick={cerrar} />}>
            Borrar usuario en Burlington
        </ModalHeader>
        <ModalBody>
            {fase !== 'resultado' && <>
                <FormGroup>
                    <Label for="borrarBurlingtonEmail">Email del usuario a borrar en Burlington</Label>
                    <Input
                        id="borrarBurlingtonEmail"
                        type="email"
                        autoFocus={true}
                        disabled={fase !== 'edicion'}
                        placeholder={fase === 'cargando' ? 'Cargando…' : ''}
                        value={email}
                        onChange={e => setEmail(e.currentTarget.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter' && fase === 'edicion' && emailLimpio.length !== 0) {
                                borrar();
                            }
                        }}
                    />
                </FormGroup>

                <div className="alert alert-warning mb-0">
                    <i className="bi bi-exclamation-triangle me-1" />
                    Se borrará el usuario en Burlington y sus licencias de Burlington pasarán a "usuario borrado".
                    Para reactivarlo hay que avisar a Soporte Burlington.
                </div>

                {error && <div className="alert alert-danger mt-3 mb-0">{error}</div>}
            </>}

            {fase === 'resultado' && resultado && <>
                <div className={`alert ${resultado.isOk ? 'alert-success' : 'alert-danger'}`}>
                    {resultado.isOk ? 'Burlington ha aceptado el borrado.' : 'No se ha podido borrar el usuario en Burlington.'}
                </div>
                <p className="mb-1"><b>Request</b></p>
                <pre className="small text-wrap">{resultado.request}</pre>
                <p className="mb-1"><b>Response</b></p>
                <pre className="small text-wrap mb-0">{resultado.response}</pre>
            </>}
        </ModalBody>
        <ModalFooter>
            {(fase === 'cargando' || fase === 'edicion') && <>
                <Button color="secondary" onClick={cerrar}>{I18n.Strings.cancel}</Button>
                <Button color="danger" disabled={fase !== 'edicion' || emailLimpio.length === 0} onClick={borrar}>
                    Borrar en Burlington
                </Button>
            </>}

            {fase === 'borrando' && <Button color="danger" disabled>
                <span className="spinner-border spinner-border-sm me-1" role="status" /> Borrando…
            </Button>}

            {fase === 'resultado' && <Button color="primary" onClick={cerrar}>Cerrar</Button>}
        </ModalFooter>
    </Modal>;
}
